import type { FieldPath, UseFormSetError } from "react-hook-form";
import type { ApplicantRequirement, ContactDetails, Lead, Person, User } from "@/types/contact";
import type { ContactCreateUserRef } from "@/types/contactCreate";
import type { ContactCreatePayload, EmailType, PhoneType } from "@/types/contactCreate";
import type { ContactSummary } from "@/types/entities";
import type { ContactInlineValidationErrors } from "./contactUniqueness";
import {
  normalizeEmailForCompare,
  normalizePhoneForCompare,
} from "./contactUniqueness";

export const INTENTION_CODE_OPTIONS = [
  { code: "0", name: "Not specified" },
  // { code: "89", name: "Not looking" },
  // { code: "90", name: "Buy" },
  // { code: "91", name: "Rent" },
  // { code: "92", name: "Buy or rent" },
  // { code: "1339", name: "Mortgage Only" },
  // { code: "1231", name: "Looking Out of Area" },
  // { code: "1437", name: "Buy to Let" },
] as const;

export function intentionNameFromCode(code: string | null | undefined): string {
  const trimmed = normalizeStoredCode(code);
  if (!trimmed) return "Not specified";
  const hit = INTENTION_CODE_OPTIONS.find((o) => o.code === trimmed);
  return hit?.name ?? "Not specified";
}

export function intentionCodeFromName(name: string | null | undefined): string | null {
  const trimmed = name?.trim().toLowerCase();
  if (!trimmed) return null;
  const hit = INTENTION_CODE_OPTIONS.find((o) => o.name.toLowerCase() === trimmed);
  return hit?.code ?? null;
}

export const LEAD_SOURCE_CODE_OPTIONS = [
  { code: "0", name: "Not specified" },
  { code: "1", name: "Local Press" },
  { code: "2", name: "Internet search" },
  { code: "7", name: "Recommendation" },
] as const;

export function leadSourceNameFromCode(code: string | null | undefined): string {
  const trimmed = normalizeStoredCode(code);
  if (!trimmed) return "Not specified";
  const hit = LEAD_SOURCE_CODE_OPTIONS.find((o) => o.code === trimmed);
  return hit?.name ?? "Not specified";
}

export function leadSourceCodeFromName(name: string | null | undefined): string | null {
  const trimmed = name?.trim().toLowerCase();
  if (!trimmed) return null;
  const hit = LEAD_SOURCE_CODE_OPTIONS.find((o) => o.name.toLowerCase() === trimmed);
  return hit?.code ?? null;
}

function normalizeStoredCode(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

/** Keep code authoritative; derive display name from code (same pattern as intention). */
function syncCodeNameFields(
  code: unknown,
  name: string | null | undefined,
  nameFromCode: (code: string | null | undefined) => string,
  codeFromName: (name: string | null | undefined) => string | null,
  defaultCode = "0",
): { code: string; name: string } {
  let resolvedCode = normalizeStoredCode(code);
  let resolvedName = name?.trim() || null;

  if (resolvedCode && !resolvedName) {
    resolvedName = nameFromCode(resolvedCode);
  } else if (!resolvedCode && resolvedName) {
    resolvedCode = codeFromName(resolvedName);
    resolvedName = resolvedCode ? nameFromCode(resolvedCode) : null;
  } else if (!resolvedCode && !resolvedName) {
    resolvedCode = defaultCode;
    resolvedName = nameFromCode(defaultCode);
  } else if (resolvedCode) {
    resolvedName = nameFromCode(resolvedCode);
  }

  return { code: resolvedCode!, name: resolvedName! };
}

function splitDisplayName(name: string): { forename: string; surname: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { forename: "", surname: "" };
  if (parts.length === 1) return { forename: parts[0], surname: "" };
  return { forename: parts[0], surname: parts.slice(1).join(" ") };
}

const TITLE_DISPLAY: Record<string, string> = {
  MR: "Mr.",
  MRS: "Mrs.",
  MS: "Ms.",
  MISS: "Miss",
  OTHER: "Other",
};

/** A person may hold up to four phone numbers. */
export const MAX_PHONE_NUMBERS_PER_PERSON = 4;

/** A person may hold up to two email addresses. */
export const MAX_EMAIL_ADDRESSES_PER_PERSON = 2;

export function emptyEmailStub(): Person["emailAddresses"][0] {
  return {
    id: "",
    atlasCreatedAt: "",
    atlasModifiedAt: "",
    externalId: "",
    emailAddress: "",
    type: "PERSONAL",
    preferenceOrder: 1,
  };
}

export function emptyPhoneStub(): Person["phoneNumbers"][0] {
  return {
    id: "",
    atlasCreatedAt: "",
    atlasModifiedAt: "",
    externalId: "",
    number: "",
    type: "HOME",
    preferenceOrder: 1,
    description: "",
    // Form-only dial-code field; defaults to UK when creating a contact.
    countryDialCode: "+44",
  };
}

export function emptyPersonStub(): Person {
  return {
    id: "",
    atlasCreatedAt: "",
    atlasModifiedAt: "",
    externalId: "",
    title: "MR",
    forename: "",
    surname: "",
    links: [],
    emailAddresses: [emptyEmailStub()],
    phoneNumbers: [emptyPhoneStub()],
  };
}

export function padPersonsToTwo(persons: Person[]): Person[] {
  const list = persons.map(ensurePersonContactArrays);
  while (list.length < 2) {
    list.push(ensurePersonContactArrays(emptyPersonStub()));
  }
  return list.slice(0, 2);
}

export function formatPersonTitle(title?: string | null): string {
  if (!title) return "";
  return TITLE_DISPLAY[title] ?? title;
}

/** Default salutation from first/second person names. */
export function buildDefaultSalutation(persons: Person[] | undefined): string {
  const segments = (persons ?? [])
    .map((p) => {
      const name = [p.forename, p.surname].map((s) => (s ?? "").trim()).filter(Boolean).join(" ");
      if (!name) return "";
      const title = formatPersonTitle(p.title);
      return title ? `${title} ${name}` : name;
    })
    .filter(Boolean);

  if (segments.length === 0) return "";
  if (segments.length === 1) return `Dear ${segments[0]}`;
  return `Dear ${segments.slice(0, -1).join(" and ")} and ${segments[segments.length - 1]}`;
}

export function personHasContent(person: Person): boolean {
  return Boolean(
    (person.forename ?? "").trim() ||
    (person.surname ?? "").trim() ||
    hasAnyEmailAddress(person.emailAddresses) ||
    hasAnyPhoneNumber(person.phoneNumbers),
  );
}

/** Optional persons (index > 0) are only sent when both email and phone are provided. */
function optionalPersonIsComplete(person: Person): boolean {
  const email = person.emailAddresses?.[0]?.emailAddress?.trim() ?? "";
  const phone = person.phoneNumbers?.[0]?.number?.trim() ?? "";
  return Boolean(email && phone);
}

export function shouldIncludePersonInPayload(person: Person, index: number): boolean {
  if (!personHasContent(person)) return false;
  if (index === 0) return true;
  return optionalPersonIsComplete(person);
}

/** Create contact: primary person needs surname; second person included when enabled with surname. */
export function shouldIncludePersonInCreatePayload(
  person: Person,
  index: number,
  secondPersonEnabled = false,
): boolean {
  const surname = (person.surname ?? "").trim();
  if (index === 0) return Boolean(surname);
  if (!secondPersonEnabled) return false;
  return Boolean(surname);
}

export function defaultApplicantRequirement(): ApplicantRequirement {
  return {
    id: "",
    externalId: null,
    links: null,
    profileIntention: null,
    isNewBuild: null,
    priceFrom: null,
    priceTo: null,
    bedsMin: null,
    isActiveProfile: true,
    rentalIntentionPriceQualifier: null,
    rentalIntentionFurnishing: null,
  };
}

function applicantRequirementHasContent(req: ApplicantRequirement): boolean {
  return Boolean(
    req.profileIntention?.trim() ||
      req.priceFrom != null ||
      req.priceTo != null ||
      req.bedsMin != null ||
      req.isNewBuild != null ||
      req.rentalIntentionFurnishing?.trim() ||
      req.rentalIntentionPriceQualifier?.trim(),
  );
}

function buildApplicantRequirementForApi(
  req: ApplicantRequirement,
  existing?: ApplicantRequirement | null,
): ApplicantRequirement {
  const id = existing?.id?.trim() || req.id?.trim() || "";
  const externalId = existing?.externalId?.trim() || req.externalId?.trim() || null;

  return {
    id,
    ...(externalId ? { externalId } : {}),
    profileIntention: req.profileIntention?.trim() || null,
    isNewBuild: req.isNewBuild ?? null,
    priceFrom: req.priceFrom ?? null,
    priceTo: req.priceTo ?? null,
    bedsMin: req.bedsMin ?? null,
    isActiveProfile: req.isActiveProfile ?? null,
    rentalIntentionPriceQualifier: req.rentalIntentionPriceQualifier?.trim() || null,
    rentalIntentionFurnishing: req.rentalIntentionFurnishing?.trim() || null,
  };
}

/**
 * Requirements are paired with the server's copies by `id`, never by array position:
 * the form can hold a seeded blank row or a newly added one, which shifts every index
 * and made `buildApplicantRequirementForApi` carry a neighbouring requirement's
 * `id`/`externalId` onto the wrong record. A form row without an id is new and has no
 * server counterpart.
 */
function buildApplicantRequirementsForApi(
  formRequirements: ApplicantRequirement[] | null | undefined,
  existingRequirements?: ApplicantRequirement[] | null,
): ApplicantRequirement[] {
  const existingById = new Map<string, ApplicantRequirement>();
  for (const existing of existingRequirements ?? []) {
    const id = existing.id?.trim();
    if (id) existingById.set(id, existing);
  }

  return (formRequirements ?? [])
    .map((req) => {
      const id = req.id?.trim();
      return buildApplicantRequirementForApi(req, (id && existingById.get(id)) || null);
    })
    .filter(applicantRequirementHasContent);
}

function ensurePersonContactArrays(person: Person): Person {
  const copy = { ...person };
  if (!copy.emailAddresses?.length) {
    copy.emailAddresses = [emptyEmailStub()];
  } else if (copy.emailAddresses.length > MAX_EMAIL_ADDRESSES_PER_PERSON) {
    copy.emailAddresses = copy.emailAddresses.slice(0, MAX_EMAIL_ADDRESSES_PER_PERSON);
  }
  if (!copy.phoneNumbers?.length) {
    copy.phoneNumbers = [emptyPhoneStub()];
  } else if (copy.phoneNumbers.length > MAX_PHONE_NUMBERS_PER_PERSON) {
    copy.phoneNumbers = copy.phoneNumbers.slice(0, MAX_PHONE_NUMBERS_PER_PERSON);
  }
  return copy;
}

/** Blank form for New contact — must reset every field so nothing carries over from edit. */
export function getEmptyContactFormDefaults(): ContactDetails {
  const person = [emptyPersonStub(), emptyPersonStub()];
  return {
    id: "",
    atlasCreatedAt: "",
    atlasModifiedAt: "",
    externalId: null,
    sourceCrm: "",
    name: "",
    title: "MR",
    forename: "",
    surname: "",
    dateOfBirth: null,
    homePhone: null,
    workPhone: null,
    mobilePhone: null,
    email: null,
    fromArchive: false,
    marketingConsent: null,
    addressSubDwelling: "",
    addressNameNumber: "",
    street: "",
    locality: "",
    town: "",
    county: "",
    postcode: "",
    countryCode: "GB",
    uprn: "",
    externalLeadId: null,
    category: "UNKNOWN",
    leadSourceCode: null,
    leadSourceName: null,
    leadId: null,
    positionCode: null,
    positionName: null,
    intentionCode: "0",
    intentionName: null,
    disposalCode: null,
    disposalName: null,
    notes: "",
    salutationLine: buildDefaultSalutation(person),
    contactNotes: null,
    communicationPreferenceLetter: null,
    communicationPreferenceEmail: null,
    communicationPreferencePhone: null,
    communicationPreferenceSms: null,
    identityCheck: null,
    additionalContactDetails: null,
    users: null,
    archived: false,
    companyName: "",
    legalEntityId: "",
    legalEntity: { bankAccountReference: "" } as ContactDetails["legalEntity"],
    links: [],
    person,
    applicantRequirements: [defaultApplicantRequirement()],
    associations: null,
    branchId: null,
    externalBranchId: null,
    lead: null,
    jobId: "",
  };
}

export function normalizeContactForForm(data: ContactDetails): ContactDetails {
  const normalized: ContactDetails = {
    ...data,
    legalEntity: data.legalEntity ?? ({ bankAccountReference: "" } as ContactDetails["legalEntity"]),
  };

  if (contactCategorySupportsRequirements(normalized.category)) {
    if (!normalized.applicantRequirements || normalized.applicantRequirements.length === 0) {
      normalized.applicantRequirements = [defaultApplicantRequirement()];
    }
  } else {
    normalized.applicantRequirements = [];
  }

  // Keep intention code authoritative; fill missing name from code, or code from name.
  const intention = syncCodeNameFields(
    normalized.intentionCode,
    normalized.intentionName,
    intentionNameFromCode,
    intentionCodeFromName,
  );
  normalized.intentionCode = intention.code;
  normalized.intentionName = intention.name;

  const leadSource = syncCodeNameFields(
    normalized.leadSourceCode,
    normalized.leadSourceName,
    leadSourceNameFromCode,
    leadSourceCodeFromName,
  );
  normalized.leadSourceCode = leadSource.code;
  normalized.leadSourceName = leadSource.name;

  let persons = (data.person ?? []).map((person) =>
    ensurePersonContactArrays(withoutDeletedContactPoints(person)),
  );

  if (persons.length === 0) {
    const fromName = data.name ? splitDisplayName(data.name) : { forename: "", surname: "" };
    const forename = (data.forename ?? fromName.forename).trim();
    const surname = (data.surname ?? fromName.surname).trim();
    const email = data.email?.trim() ?? "";
    const mobile = data.mobilePhone?.trim() ?? "";

    persons = [
      ensurePersonContactArrays({
        id: "",
        atlasCreatedAt: "",
        atlasModifiedAt: "",
        externalId: "",
        title: data.title ?? "MR",
        forename,
        surname,
        links: [],
        emailAddresses: email
          ? [{ id: "", atlasCreatedAt: "", atlasModifiedAt: "", externalId: "", emailAddress: email, type: "PERSONAL", preferenceOrder: 1 }]
          : [],
        phoneNumbers: mobile
          ? [
            {
              id: "",
              atlasCreatedAt: "",
              atlasModifiedAt: "",
              externalId: "",
              number: mobile,
              type: "HOME",
              preferenceOrder: 1,
              description: "",
            },
          ]
          : [],
      }),
    ];
  }

  normalized.person = padPersonsToTwo(persons);
  if (!normalized.salutationLine?.trim()) {
    normalized.salutationLine = buildDefaultSalutation(normalized.person);
  }
  return normalized;
}

export function contactDetailsToSummary(d: ContactDetails): ContactSummary {
  const primary = d.person?.[0];
  const name =
    d.name?.trim() ||
    `${d.forename ?? primary?.forename ?? ""} ${d.surname ?? primary?.surname ?? ""}`.trim() ||
    null;

  return {
    id: d.id,
    sourceCrm: d.sourceCrm,
    externalId: d.externalId ?? "",
    name,
    forename: (d.forename ?? primary?.forename)?.trim() || null,
    surname: (d.surname ?? primary?.surname)?.trim() || null,
    email: d.email ?? (firstLiveEmailAddress(primary?.emailAddresses) || null),
    mobilePhone: d.mobilePhone ?? (firstLivePhoneNumber(primary?.phoneNumbers) || null),
    town: d.town ?? null,
    postcode: d.postcode ?? null,
    category: d.category ?? null,
    externalBranchId:
      d.associations?.[0]?.externalBranchId ??
      (() => {
        const w = d.externalBranchId;
        if (w == null) return null;
        if (Array.isArray(w)) return w.map((x) => String(x).trim()).find(Boolean) ?? null;
        const one = String(w).trim();
        return one || null;
      })() ??
      null,
    archived: d.archived ?? false,
    atlasModifiedAt: d.atlasModifiedAt ?? null,
  };
}

/** POST /contacts may omit top-level name/phone — fill from the create payload. */
export function contactCreateResponseToSummary(
  raw: ContactDetails,
  payload: ContactCreatePayload,
): ContactSummary {
  const summary = contactDetailsToSummary(raw);
  const person = payload.person[0];
  if (!person) return summary;

  const name =
    summary.name?.trim() ||
    `${person.forename} ${person.surname}`.trim() ||
    null;
  const mobilePhone =
    summary.mobilePhone?.trim() ||
    person.phoneNumbers?.find((p) => p.type === "MOBILE")?.number?.trim() ||
    person.phoneNumbers?.[0]?.number?.trim() ||
    null;
  const email =
    summary.email?.trim() ||
    person.emailAddresses?.[0]?.emailAddress?.trim() ||
    null;

  return {
    ...summary,
    name,
    forename: summary.forename ?? person.forename,
    surname: summary.surname ?? person.surname,
    mobilePhone,
    email: email ?? summary.email,
  };
}

// --- Contact create / update API payloads ----------------------------------

/** Phone type dropdown (Alto-style labels). */
export const PHONE_TYPE_SELECT_OPTIONS = [
  { value: "MOBILE", label: "Mob" },
  { value: "WORK", label: "Work" },
  { value: "HOME", label: "Home" },
  { value: "BUSINESS", label: "Bus" },
  { value: "FAX", label: "Fax" },
] as const;

/** Phone description sent to API (e.g. `"MOBILE"` alongside `type: "HOME"`). */
export const PHONE_DESCRIPTION_SELECT_OPTIONS = [
  { value: "MOBILE", label: "Mob" },
  { value: "WORK", label: "Work" },
  { value: "HOME", label: "Home" },
  { value: "BUSINESS", label: "Bus" },
  { value: "FAX", label: "Fax" },
] as const;

const PHONE_TYPE_MAP: Record<string, PhoneType> = {
  UNSPECIFIED: "UNSPECIFIED",
  HOME: "HOME",
  BUSINESS: "BUSINESS",
  MOBILE: "MOBILE",
  FAX: "FAX",
  WORK: "WORK",
  ISDN: "ISDN",
  Mobile: "MOBILE",
  mobile: "MOBILE",
  Home: "HOME",
  home: "HOME",
  Business: "BUSINESS",
  business: "BUSINESS",
  Work: "WORK",
  work: "WORK",
};

export const EMAIL_TYPE_SELECT_OPTIONS = [
  { value: "PERSONAL", label: "Personal" },
  { value: "BUSINESS", label: "Business" },
] as const;

const EMAIL_TYPE_MAP: Record<string, EmailType> = {
  UNKNOWN: "UNKNOWN",
  PERSONAL: "PERSONAL",
  BUSINESS: "BUSINESS",
  Personal: "PERSONAL",
  personal: "PERSONAL",
  Business: "BUSINESS",
  business: "BUSINESS",
};

function normalizePhoneType(value?: string | null): PhoneType {
  if (!value) return "HOME";
  return PHONE_TYPE_MAP[value] ?? PHONE_TYPE_MAP[value.toUpperCase()] ?? "HOME";
}

function normalizePhoneDescription(value?: string | null): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  const known = PHONE_DESCRIPTION_SELECT_OPTIONS.find((o) => o.value === upper);
  return known?.value ?? trimmed;
}

export function phoneSelectOptionsWithValue(
  options: readonly { value: string; label: string }[],
  value: string | null | undefined,
): { value: string; label: string }[] {
  const v = (value ?? "").trim();
  if (!v) return [...options];
  const upper = v.toUpperCase();
  if (options.some((o) => o.value === upper || o.value === v)) return [...options];
  return [...options, { value: upper, label: v }];
}

function normalizeEmailType(value?: string | null): EmailType {
  if (!value) return "PERSONAL";
  return EMAIL_TYPE_MAP[value] ?? EMAIL_TYPE_MAP[value.toUpperCase()] ?? "PERSONAL";
}

function omitEmpty<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined || value === "") continue;
    out[key] = value;
  }
  return out as Partial<T>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Full phone with optional leading + and digits only (e.g. profile company phone). */
export const MOBILE_PHONE_REGEX = /^\+?[0-9]{10,25}$/;
/** National number field (dial code selected separately): digits only. */
export const NATIONAL_PHONE_REGEX = /^[0-9]{10,20}$/;
/** Contact phone values may contain spaces and an optional leading +. */
export const CONTACT_MOBILE_PHONE_REGEX = /^[+]?[0-9\s]{10,20}$/;
export const CONTACT_MOBILE_PHONE_VALIDATION_MESSAGE =
  "Enter a valid mobile number (10–20 digits; spaces and an optional + are allowed)";
export const MOBILE_PHONE_VALIDATION_MESSAGE =
  "Enter a valid number (10–20 digits, numbers only)";

export function sanitizeNationalPhoneInput(value: string): string {
  return value.replace(/\D/g, "");
}

/** ISO 3166-1 alpha-2 (two le  tters). */
export const COUNTRY_CODE_REGEX = /^[A-Za-z]{2}$/;
export const COUNTRY_CODE_VALIDATION_MESSAGE =
  "Country code must be exactly 2 letters (e.g. GB, HK)";

export function normalizeCountryCode(value: string | null | undefined): string {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 2);
}

/** Split a phone number like "+919876543211" into { dialCode: "+91", national: "9876543211" }.
 *  Tries longest-match against known dial codes so "+1" vs "+91" resolve correctly. */
export function extractDialCode(
  number: string | null | undefined,
  dialCodes: string[],
): { dialCode: string; national: string } {
  const raw = (number ?? "").trim();
  if (!raw.startsWith("+")) return { dialCode: "", national: raw };
  const sorted = [...dialCodes].sort((a, b) => b.length - a.length);
  for (const code of sorted) {
    if (raw.startsWith(code)) {
      return { dialCode: code, national: raw.slice(code.length) };
    }
  }
  return { dialCode: "", national: raw };
}

const MAJOR_COUNTRY_FOR_DIAL_CODE: Record<string, string> = {
  "+1": "US",
  "+44": "GB",
  "+7": "RU",
  "+61": "AU",
  "+64": "NZ",
  "+39": "IT",
  "+358": "FI",
  "+47": "NO",
  "+212": "MA",
  "+590": "GP",
  "+262": "RE",
};

/** Resolve which `phoneCodeOption.value` (e.g. "+44|GB") best matches a raw dial code.
 *  Priority: 1) exact dial+country match using the form's countryCode,
 *            2) major country fallback,
 *            3) first match. */
export function resolvePhoneCodeOptionValue(
  rawDialCode: string,
  formCountryCode: string,
  phoneCodeOptions: { value: string }[],
): string {
  if (!rawDialCode) return "";
  const matches = phoneCodeOptions.filter((o) => o.value.startsWith(`${rawDialCode}|`));
  if (matches.length === 0) return rawDialCode;
  if (matches.length === 1) return matches[0].value;

  if (formCountryCode) {
    const byCountry = matches.find((o) => o.value === `${rawDialCode}|${formCountryCode}`);
    if (byCountry) return byCountry.value;
  }

  const major = MAJOR_COUNTRY_FOR_DIAL_CODE[rawDialCode];
  if (major) {
    const byMajor = matches.find((o) => o.value === `${rawDialCode}|${major}`);
    if (byMajor) return byMajor.value;
  }

  return matches[0].value;
}

export const CATEGORY_REQUIRED_MESSAGE = "Contact type is required";

/** ALTO: Contact Relationship contacts cannot carry applicant requirements. */
export function contactCategorySupportsRequirements(category: string | null | undefined): boolean {
  const normalized = (category ?? "").trim().toUpperCase().replace(/\s+/g, "");
  return normalized !== "CONTACTRELATIONSHIP";
}
export const FORENAME_REQUIRED_MESSAGE = "First name is required";
export const SURNAME_REQUIRED_MESSAGE = "Surname is required";
export const EMAIL_REQUIRED_MESSAGE = "Email is required";
export const PHONE_REQUIRED_MESSAGE = "Mobile number is required";
export const POSTCODE_REQUIRED_MESSAGE = "";

export interface ContactFormValidationInput {
  countryCode: string;
  category: string;
  forename: string;
  surname: string;
  email: string;
  phone: string;
  postcode: string;
}

export interface ContactCreateValidationInput extends ContactFormValidationInput {
  legalEntityId: string;
  branchId: string;
  externalBranchId: string;
  /** From GET /branches/{id} — required so we never invent a CRM in the UI. */
  sourceCrm: string;
  /** Selected Atlas user id; required on create when the branch's CRM is Reapit. Omit for forms with no user field. */
  userId?: string;
  secondSurname?: string;
  secondEmail?: string;
  secondPhone?: string;
  /** When true, second person surname is required on create. */
  secondPersonEnabled?: boolean;
}

export type ContactFormValidationResult =
  | { success: true }
  | { success: false; message: string };

function validateContactPersonFields(
  input: ContactFormValidationInput & {
    secondSurname?: string;
    secondEmail?: string;
    secondPhone?: string;
    secondPersonEnabled?: boolean;
  },
): ContactFormValidationResult {
  if (!input.surname.trim()) {
    return { success: false, message: SURNAME_REQUIRED_MESSAGE };
  }

  const email = input.email.trim();
  if (email && !EMAIL_REGEX.test(email)) {
    return { success: false, message: "Enter a valid email address" };
  }
  const countryCode = input.countryCode.trim();
  if (countryCode && !COUNTRY_CODE_REGEX.test(countryCode)) {
    return { success: false, message: COUNTRY_CODE_VALIDATION_MESSAGE };
  }

  if (input.secondPersonEnabled && !input.secondSurname?.trim()) {
    return { success: false, message: SURNAME_REQUIRED_MESSAGE };
  }
  if (input.secondPersonEnabled) {
    const secondEmail = input.secondEmail?.trim() ?? "";
    if (secondEmail && !EMAIL_REGEX.test(secondEmail)) {
      return { success: false, message: "Enter a valid email address" };
    }
  }

  return { success: true };
}

export const EMAIL_REQUIRED_FOR_PERSON_MESSAGE =
  "Each person needs at least one email address";
export const PHONE_REQUIRED_FOR_PERSON_MESSAGE =
  "Each person needs at least one phone number";

/**
 * Rows the user removed are kept in the form until save (they carry `isDeleted`),
 * so everything that reads the "current" contact points has to skip them.
 * The API returns the same state as `deleted` on read.
 */
export function isContactPointDeleted(
  entry: { isDeleted?: boolean; deleted?: boolean } | null | undefined,
): boolean {
  return Boolean(entry?.isDeleted || entry?.deleted);
}

/** Drop contact points the API marks as deleted before hydrating the form. */
function withoutDeletedContactPoints(person: Person): Person {
  return {
    ...person,
    emailAddresses: (person.emailAddresses ?? []).filter((entry) => !isContactPointDeleted(entry)),
    phoneNumbers: (person.phoneNumbers ?? []).filter((entry) => !isContactPointDeleted(entry)),
  };
}

/** True when any row still in play carries an address. */
export function hasAnyEmailAddress(list: Person["emailAddresses"] | undefined): boolean {
  return (list ?? []).some(
    (entry) => !isContactPointDeleted(entry) && Boolean((entry?.emailAddress ?? "").trim()),
  );
}

/** True when any row still in play carries a number. */
export function hasAnyPhoneNumber(list: Person["phoneNumbers"] | undefined): boolean {
  return (list ?? []).some(
    (entry) => !isContactPointDeleted(entry) && Boolean((entry?.number ?? "").trim()),
  );
}

/** First address still in play — rows flagged for deletion are skipped. */
export function firstLiveEmailAddress(list: Person["emailAddresses"] | undefined): string {
  const entry = (list ?? []).find(
    (em) => !isContactPointDeleted(em) && Boolean((em?.emailAddress ?? "").trim()),
  );
  return (entry?.emailAddress ?? "").trim();
}

/** First number still in play, dial code included — rows flagged for deletion are skipped. */
export function firstLivePhoneNumber(list: Person["phoneNumbers"] | undefined): string {
  const entry = (list ?? []).find(
    (ph) => !isContactPointDeleted(ph) && Boolean((ph?.number ?? "").trim()),
  );
  return entry ? buildFullPhoneNumber(entry) : "";
}

/**
 * Shared person validation for update — surname is mandatory, and a saved person
 * must keep at least one email and one phone. The `has*` flags are optional so
 * callers that don't track the full arrays keep the surname-only behaviour.
 */
export function validateContactFormInput(
  input: ContactFormValidationInput & {
    secondSurname?: string;
    secondEmail?: string;
    secondPhone?: string;
    secondPersonEnabled?: boolean;
    hasEmail?: boolean;
    hasPhone?: boolean;
    secondHasEmail?: boolean;
    secondHasPhone?: boolean;
  },
): ContactFormValidationResult {
  const personValidation = validateContactPersonFields(input);
  if (!personValidation.success) return personValidation;

  if (input.hasEmail === false) {
    return { success: false, message: EMAIL_REQUIRED_FOR_PERSON_MESSAGE };
  }
  if (input.hasPhone === false) {
    return { success: false, message: PHONE_REQUIRED_FOR_PERSON_MESSAGE };
  }
  if (input.secondPersonEnabled) {
    if (input.secondHasEmail === false) {
      return { success: false, message: EMAIL_REQUIRED_FOR_PERSON_MESSAGE };
    }
    if (input.secondHasPhone === false) {
      return { success: false, message: PHONE_REQUIRED_FOR_PERSON_MESSAGE };
    }
  }

  return { success: true };
}

export const USER_REQUIRED_MESSAGE = "Select a user";

/** Reapit requires a negotiator (user) on every contact. */
export function isUserRequiredForCrm(sourceCrm?: string | null): boolean {
  return (sourceCrm ?? "").trim().toUpperCase() === "REAPIT";
}

export function validateContactCreateInput(
  input: ContactCreateValidationInput,
): ContactFormValidationResult {
  const personValidation = validateContactPersonFields(input);
  if (!personValidation.success) return personValidation;

  if (input.phone.trim() && !CONTACT_MOBILE_PHONE_REGEX.test(input.phone.trim())) {
    return { success: false, message: CONTACT_MOBILE_PHONE_VALIDATION_MESSAGE };
  }
  if (input.secondPhone?.trim() && !CONTACT_MOBILE_PHONE_REGEX.test(input.secondPhone.trim())) {
    return { success: false, message: CONTACT_MOBILE_PHONE_VALIDATION_MESSAGE };
  }

  if (!input.legalEntityId.trim()) {
    return { success: false, message: "Select a legal entity in the header" };
  }
  if (!input.branchId.trim()) {
    return { success: false, message: "Select a branch" };
  }
  if (!input.externalBranchId.trim()) {
    return { success: false, message: "Branch external id is missing" };
  }
  if (!input.sourceCrm.trim()) {
    return {
      success: false,
      message: "Source CRM was not returned for this branch. Wait for data to load, or verify the branch in the API.",
    };
  }
  if (input.userId !== undefined && isUserRequiredForCrm(input.sourceCrm) && !input.userId.trim()) {
    return { success: false, message: USER_REQUIRED_MESSAGE };
  }
  return { success: true };
}

const CONTACT_UNIQUENESS_FIELD_PATHS = {
  email: "person.0.emailAddresses.0.emailAddress",
  mobilePhone: "person.0.phoneNumbers.0.number",
  email2: "person.1.emailAddresses.0.emailAddress",
  mobilePhone2: "person.1.phoneNumbers.0.number",
} as const satisfies Record<keyof ContactInlineValidationErrors, FieldPath<ContactDetails>>;

/** Maps inline uniqueness errors to react-hook-form field paths. */
export function applyContactInlineValidationToForm(
  setError: UseFormSetError<ContactDetails>,
  clearErrors: (name?: FieldPath<ContactDetails> | FieldPath<ContactDetails>[]) => void,
  inlineErrors: ContactInlineValidationErrors,
): void {
  const paths = Object.values(CONTACT_UNIQUENESS_FIELD_PATHS);
  clearErrors(paths);

  (Object.keys(CONTACT_UNIQUENESS_FIELD_PATHS) as (keyof ContactInlineValidationErrors)[]).forEach(
    (key) => {
      const message = inlineErrors[key];
      if (!message) return;
      setError(CONTACT_UNIQUENESS_FIELD_PATHS[key], { type: "manual", message });
    },
  );
}

/** Maps Atlas POST/PATCH validation messages to form fields (backend source of truth). */
export function parseContactApiValidationErrors(apiMessage: string): Array<{
  name: FieldPath<ContactDetails>;
  message: string;
}> {
  const message = apiMessage.trim();
  if (!message) return [];

  const results: Array<{ name: FieldPath<ContactDetails>; message: string }> = [];

  if (/email/i.test(message) && /already in use/i.test(message)) {
    results.push({
      name: "person.0.emailAddresses.0.emailAddress",
      message: "Email already exists.",
    });
  }

  if (/phone/i.test(message) && /already in use/i.test(message)) {
    results.push({
      name: "person.0.phoneNumbers.0.number",
      message: "Mobile number already exists.",
    });
  }

  const mandatoryMatch = message.match(/missing mandatory fields:\s*(.+)$/i);
  if (mandatoryMatch) {
    const fields = mandatoryMatch[1].split(",").map((f) => f.trim());
    if (fields.includes("postcode")) {
      results.push({ name: "postcode", message: POSTCODE_REQUIRED_MESSAGE });
    }
    if (fields.includes("category")) {
      results.push({ name: "category", message: CATEGORY_REQUIRED_MESSAGE });
    }
  }

  if (/person name/i.test(message) && /street/i.test(message)) {
    results.push({ name: "street", message: "Contact with same person name and street already exists" });
  }

  return results;
}

export function applyContactApiValidationToForm(
  setError: UseFormSetError<ContactDetails>,
  apiMessage: string,
): boolean {
  const fieldErrors = parseContactApiValidationErrors(apiMessage);
  for (const { name, message } of fieldErrors) {
    setError(name, { type: "server", message });
  }
  return fieldErrors.length > 0;
}

export interface BuildContactCreateContext {
  legalEntityId: string;
  branchId: string;
  externalBranchId: string;
  /** From GET /branches/{id}; identifies CRM (e.g. Alto) for new contacts. */
  sourceCrm?: string | null;
  /** Optional assigned user from GET /users. */
  user?: { id: string; externalId: string; name: string | null } | null;
  /** Any pre-existing users (e.g., duplicating a contact). */
  existingUsers?: ContactDetails["users"] | null;
  /** When false, second person is omitted from POST /contacts. */
  secondPersonEnabled?: boolean;
}

export type ContactCreateFormInput = Pick<
  ContactDetails,
  | "sourceCrm"
  | "category"
  | "companyName"
  | "countryCode"
  | "intentionName"
  | "intentionCode"
  | "positionCode"
  | "positionName"
  | "leadSourceCode"
  | "leadSourceName"
  | "notes"
  | "addressSubDwelling"
  | "addressNameNumber"
  | "street"
  | "locality"
  | "town"
  | "county"
  | "postcode"
  | "uprn"
  | "person"
  | "applicantRequirements"
>;

function trimOrNull(value: string | null | undefined): string | null {
  const t = value?.trim();
  return t ? t : null;
}

function buildFullPhoneNumber(phoneEntry: Person["phoneNumbers"][0]): string {
  const raw = (phoneEntry?.number ?? "").trim();
  if (!raw) return "";
  const dialCode = ((phoneEntry as any)?.countryDialCode ?? "").trim();
  if (!dialCode || raw.startsWith("+")) return raw;
  const national = raw.replace(/^0+/, "");
  return `${dialCode}${national}`;
}

/**
 * Non-empty phone rows in form order, capped at {@link MAX_PHONE_NUMBERS_PER_PERSON}.
 * `preferenceOrder` follows position, so the order shown in the form round-trips.
 */
function formPhoneEntries(person: Person): Array<{
  entry: Person["phoneNumbers"][0];
  number: string;
  index: number;
  preferenceOrder: number;
}> {
  return (person.phoneNumbers ?? [])
    .slice(0, MAX_PHONE_NUMBERS_PER_PERSON)
    .map((entry, index) => ({ entry, number: buildFullPhoneNumber(entry), index }))
    .filter((row) => Boolean(row.number) && !isContactPointDeleted(row.entry))
    .map((row, position) => ({ ...row, preferenceOrder: position + 1 }));
}

/**
 * Non-empty email rows in form order, capped at {@link MAX_EMAIL_ADDRESSES_PER_PERSON}.
 * `preferenceOrder` follows position, so the order shown in the form round-trips.
 */
function formEmailEntries(person: Person): Array<{
  entry: Person["emailAddresses"][0];
  emailAddress: string;
  index: number;
  preferenceOrder: number;
}> {
  return (person.emailAddresses ?? [])
    .slice(0, MAX_EMAIL_ADDRESSES_PER_PERSON)
    .map((entry, index) => ({ entry, emailAddress: (entry?.emailAddress ?? "").trim(), index }))
    .filter((row) => Boolean(row.emailAddress) && !isContactPointDeleted(row.entry))
    .map((row, position) => ({ ...row, preferenceOrder: position + 1 }));
}

/** Person shape for POST /contacts (nested phone/email with `id: null`). */
function buildPersonForCreateApi(person: Person) {
  const forename = (person.forename ?? "").trim();
  const surname = (person.surname ?? "").trim();

  return {
    title: person.title || "MR",
    forename,
    surname,
    emailAddresses: formEmailEntries(person).map(({ entry, emailAddress, preferenceOrder }) => ({
      id: null,
      preferenceOrder,
      emailAddress,
      type: normalizeEmailType(entry?.type),
    })),
    phoneNumbers: formPhoneEntries(person).map(({ entry, number, preferenceOrder }) => {
      const type = normalizePhoneType(entry?.type);
      return {
        id: null,
        preferenceOrder,
        type,
        number,
        description: normalizePhoneDescription(entry?.description) ?? type,
      };
    }),
  };
}

export function buildContactCreatePayload(
  form: ContactCreateFormInput,
  ctx: BuildContactCreateContext,
): ContactCreatePayload {
  if (!form.person?.some((p, index) => shouldIncludePersonInCreatePayload(p as Person, index, ctx.secondPersonEnabled))) {
    throw new Error(SURNAME_REQUIRED_MESSAGE);
  }

  const sourceCrm = form.sourceCrm?.trim() || ctx.sourceCrm?.trim();
  if (!sourceCrm) {
    throw new Error("sourceCrm is required — it must come from the branch API");
  }

  const payload: ContactCreatePayload = {
    sourceCrm,
    legalEntityId: ctx.legalEntityId,
    header: null,
    bridgeBranchId: null,
    externalBranchId: [ctx.externalBranchId],
    externalLeadId: null,
    branchId: [ctx.branchId],
    addressSubDwelling: trimOrNull(form.addressSubDwelling),
    addressNameNumber: trimOrNull(form.addressNameNumber),
    street: trimOrNull(form.street),
    locality: trimOrNull(form.locality),
    town: trimOrNull(form.town),
    county: trimOrNull(form.county),
    postcode: trimOrNull(form.postcode),
    countryCode: normalizeCountryCode(form.countryCode),
    uprn: trimOrNull(form.uprn ?? undefined),
    category: (form.category?.trim() || "UNKNOWN") as ContactCreatePayload["category"],
    companyName: trimOrNull(form.companyName),
    intentionName: trimOrNull(form.intentionName),
    positionName: trimOrNull(form.positionName),
    leadSourceName: trimOrNull(form.leadSourceName),
    leadSourceCode:
      normalizeStoredCode(form.leadSourceCode) ??
      leadSourceCodeFromName(form.leadSourceName) ??
      null,
    ...omitEmpty({
      intentionCode:
        normalizeStoredCode(form.intentionCode) ??
        intentionCodeFromName(form.intentionName) ??
        "0",
      positionCode: normalizeStoredCode(form.positionCode),
      notes: form.notes,
    }),
    person: (form.person ?? [])
      .map((p, index) => ({ person: p as Person, index }))
      .filter(({ person, index }) =>
        shouldIncludePersonInCreatePayload(person, index, ctx.secondPersonEnabled),
      )
      .map(({ person }) => buildPersonForCreateApi(person)),
  };

  if (ctx.user?.id) {
    const seenIds = new Set<string>();
    const users: NonNullable<ContactCreatePayload["users"]> = [];

    const pushUser = (u: { id?: string | null; externalId?: string | null; name?: string | null }) => {
      const id = u.id?.trim();
      if (!id) return; // ContactCreateUserRef requires id
      if (seenIds.has(id)) return;
      seenIds.add(id);
      const externalId = u.externalId?.trim();
      users.push({
        id,
        ...(externalId ? { externalId } : {}),
        ...(u.name !== undefined ? { name: u.name } : {}),
      });
    };

    // include any existing users already linked to the contact (for duplication flows)
    for (const u of normalizeContactUsersList(ctx.existingUsers)) {
      pushUser({ id: u.id, externalId: (u as any)?.externalId, name: (u as any)?.name ?? null });
    }

    // ensure the newly selected user is present (dedupes by id/externalId)
    pushUser(ctx.user);

    if (users.length) {
      payload.users = users;
    }
  }

  if (contactCategorySupportsRequirements(form.category)) {
    const requirements = buildApplicantRequirementsForApi(form.applicantRequirements);
    payload.applicantRequirements = requirements.length > 0 ? requirements : null;
  } else {
    payload.applicantRequirements = null;
  }

  return payload;
}

/**
 * Saved rows the user removed in the form. They are sent back with `deleted: true`
 * so the backend drops them as part of the update — rows that were never saved carry
 * no id and simply disappear.
 */
function deletedContactPointRows<T extends { id?: string | null; externalId?: string | null }>(
  formRows: T[] | undefined,
): Array<{ entry: T; id: string; externalId: string | null }> {
  return (formRows ?? [])
    .map((entry) => {
      const id = (entry?.id ?? "").trim();
      const externalId = (entry?.externalId ?? "").trim() || null;
      return { entry, id, externalId };
    })
    .filter(
      (row) =>
        isContactPointDeleted(row.entry as { isDeleted?: boolean; deleted?: boolean }) &&
        Boolean(row.id),
    );
}

/** Match a form row to its server record by id or externalId — not array position. */
function findExistingContactPoint<T extends { id?: string | null; externalId?: string | null }>(
  entry: T | null | undefined,
  existingRows: T[] | undefined,
  fallbackIndex?: number,
): T | undefined {
  const entryId = (entry?.id ?? "").trim();
  const entryExternalId = (entry?.externalId ?? "").trim();
  if (entryId || entryExternalId) {
    const match = (existingRows ?? []).find(
      (row) =>
        (entryId && (row.id ?? "").trim() === entryId) ||
        (entryExternalId && (row.externalId ?? "").trim() === entryExternalId),
    );
    if (match) return match;
  }
  if (fallbackIndex == null) return undefined;
  return existingRows?.[fallbackIndex];
}

function buildPersonForApi(person: Person, existing?: Person | null) {
  const forename = (person.forename ?? "").trim();
  const surname = (person.surname ?? "").trim();

  return {
    ...(person.id ? { id: person.id } : {}),
    ...(person.externalId ? { externalId: person.externalId } : {}),
    title: person.title || "MR",
    forename,
    surname,
    // Ids are matched by record id/externalId so delete-and-add flows keep the right rows.
    emailAddresses: formEmailEntries(person).map(({ entry, emailAddress, index, preferenceOrder }) => {
      const existingEmail = findExistingContactPoint(
        entry,
        existing?.emailAddresses,
        index,
      );
      return {
        ...(existingEmail?.id ? { id: existingEmail.id } : {}),
        ...(existingEmail?.externalId ? { externalId: existingEmail.externalId } : {}),
        emailAddress,
        type: normalizeEmailType(entry?.type ?? existingEmail?.type),
        preferenceOrder,
      };
    }).concat(
      deletedContactPointRows(person.emailAddresses).map(
        ({ entry, id, externalId }) => ({
          id,
          ...(externalId ? { externalId } : {}),
          emailAddress: (entry?.emailAddress ?? "").trim(),
          type: normalizeEmailType(entry?.type),
          preferenceOrder: entry?.preferenceOrder ?? 0,
          deleted: true,
        }),
      ) as never[],
    ),
    // Ids are matched by record id/externalId so delete-and-add flows keep the right rows.
    phoneNumbers: formPhoneEntries(person).map(({ entry, number, index, preferenceOrder }) => {
      const existingPhone = findExistingContactPoint(
        entry,
        existing?.phoneNumbers,
        index,
      );
      const type = normalizePhoneType(entry?.type ?? existingPhone?.type);
      const description = normalizePhoneDescription(
        entry?.description ?? existingPhone?.description,
      );
      return {
        ...(existingPhone?.id ? { id: existingPhone.id } : {}),
        ...(existingPhone?.externalId ? { externalId: existingPhone.externalId } : {}),
        number,
        type,
        ...(description ? { description } : {}),
        preferenceOrder,
      };
    }).concat(
      deletedContactPointRows(person.phoneNumbers).map(
        ({ entry, id, externalId }) => {
          const type = normalizePhoneType(entry?.type);
          const description = normalizePhoneDescription(entry?.description);
          return {
            id,
            ...(externalId ? { externalId } : {}),
            number: buildFullPhoneNumber(entry),
            type,
            ...(description ? { description } : {}),
            preferenceOrder: entry?.preferenceOrder ?? 0,
            deleted: true,
          };
        },
      ) as never[],
    ),
  };
}

export type ContactAssignedUser = {
  id: string;
  externalId: string;
  name: string | null;
};

/** Normalize `users` from GET /contacts/{id} (single object or array). */
export function normalizeContactUsersList(
  users: ContactDetails["users"] | undefined,
): User[] {
  if (!users) return [];
  return Array.isArray(users) ? users : [users];
}

/** Match assigned user to Atlas user id for the dropdown (by id or externalId). */
export function resolveContactUserAtlasId(
  data: ContactDetails | null,
  lead: Lead | null,
  usersByAtlasId: Map<string, { externalId: string }>,
): string {
  const fromUsers = normalizeContactUsersList(data?.users);
  const candidates: Array<{ id?: string; externalId?: string }> = [
    ...fromUsers,
    ...(lead?.user ? [lead.user] : []),
  ];

  for (const c of candidates) {
    const atlasId = c.id?.trim();
    if (atlasId && usersByAtlasId.has(atlasId)) return atlasId;

    const ext = c.externalId?.trim();
    if (ext) {
      for (const [id, meta] of usersByAtlasId) {
        if (meta.externalId === ext) return id;
      }
    }
  }
  return "";
}

function snapshotApplicantRequirementsForCompare(
  requirements: ApplicantRequirement[] | null | undefined,
): ApplicantRequirement[] {
  return (requirements ?? [])
    .map((req) => buildApplicantRequirementForApi(req, undefined))
    .filter(applicantRequirementHasContent);
}

function snapshotPersonForCompare(
  person: Person | undefined,
  index: number,
  secondPersonEnabled: boolean,
): Record<string, string> | null {
  if (index > 0 && !secondPersonEnabled) return null;
  const p = ensurePersonContactArrays(person ?? emptyPersonStub());
  return {
    title: (p.title ?? "MR").trim(),
    forename: (p.forename ?? "").trim(),
    surname: (p.surname ?? "").trim(),
    // Every row participates so edits below the first still mark the form dirty.
    email: (p.emailAddresses ?? [])
      .slice(0, MAX_EMAIL_ADDRESSES_PER_PERSON)
      .map((em) =>
        [
          normalizeEmailForCompare(em?.emailAddress),
          (em?.type ?? "PERSONAL").trim(),
          // A row marked for deletion is a change even though its value is untouched.
          isContactPointDeleted(em) ? "deleted" : "",
        ].join("~"),
      )
      .join("|"),
    // Every row participates, so edits below the first still mark the form dirty.
    // The dial code is included too — changing only the code is a real change.
    phones: (p.phoneNumbers ?? [])
      .slice(0, MAX_PHONE_NUMBERS_PER_PERSON)
      .map((ph) =>
        [
          normalizePhoneForCompare(ph?.number),
          (ph?.countryDialCode ?? "").trim(),
          (ph?.type ?? "HOME").trim(),
          (ph?.description ?? "").trim(),
          isContactPointDeleted(ph) ? "deleted" : "",
        ].join("~"),
      )
      .join("|"),
  };
}

function snapshotContactEditForm(
  form: ContactDetails,
  secondPersonEnabled: boolean,
): string {
  const intention = syncCodeNameFields(
    form.intentionCode,
    form.intentionName,
    intentionNameFromCode,
    intentionCodeFromName,
  );
  const leadSource = syncCodeNameFields(
    form.leadSourceCode,
    form.leadSourceName,
    leadSourceNameFromCode,
    leadSourceCodeFromName,
  );

  const payload = {
    category: (form.category?.trim() || "UNKNOWN").trim(),
    notes: (form.notes ?? "").trim(),
    companyName: (form.companyName ?? "").trim(),
    salutationLine: (form.salutationLine ?? "").trim(),
    intentionCode: intention.code,
    leadSourceCode: leadSource.code,
    positionCode: trimOrNull(form.positionCode),
    positionName: trimOrNull(form.positionName),
    addressSubDwelling: trimOrNull(form.addressSubDwelling),
    addressNameNumber: trimOrNull(form.addressNameNumber),
    street: trimOrNull(form.street),
    locality: trimOrNull(form.locality),
    town: trimOrNull(form.town),
    county: trimOrNull(form.county),
    postcode: trimOrNull(form.postcode),
    countryCode: normalizeCountryCode(form.countryCode) || null,
    uprn: trimOrNull(form.uprn ?? undefined),
    person0: snapshotPersonForCompare(form.person?.[0], 0, true),
    person1: snapshotPersonForCompare(form.person?.[1], 1, secondPersonEnabled),
    applicantRequirements: snapshotApplicantRequirementsForCompare(form.applicantRequirements),
  };

  return JSON.stringify(payload);
}

/** True when edit form differs from the snapshot taken at load/reset (ignores react-hook-form isDirty). */
export function contactEditFormHasChanges(
  form: ContactDetails,
  baseline: ContactDetails,
  ctx: { secondPersonEnabled: boolean; baselineSecondPersonEnabled: boolean },
): boolean {
  if (ctx.secondPersonEnabled !== ctx.baselineSecondPersonEnabled) return true;
  return (
    snapshotContactEditForm(form, ctx.secondPersonEnabled) !==
    snapshotContactEditForm(baseline, ctx.baselineSecondPersonEnabled)
  );
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Added / removed / changed counts for one person's phone or email rows, compared by
 * row position — the form never reorders rows, so position identifies a row.
 */
function describeContactPointChanges(
  noun: "email address" | "phone number",
  personLabel: string,
  formRows: Array<{ value: string; deleted: boolean }>,
  baselineRows: Array<{ value: string }>,
): string[] {
  let added = 0;
  let removed = 0;
  let changed = 0;

  const rowCount = Math.max(formRows.length, baselineRows.length);
  for (let index = 0; index < rowCount; index += 1) {
    const before = baselineRows[index]?.value ?? "";
    const row = formRows[index];
    const after = row && !row.deleted ? row.value : "";
    if (before === after) continue;
    if (!before) added += 1;
    else if (!after) removed += 1;
    else changed += 1;
  }

  const lines: string[] = [];
  if (added) lines.push(`Added ${pluralize(added, noun)} for ${personLabel}`);
  if (removed) lines.push(`Removed ${pluralize(removed, noun)} from ${personLabel}`);
  if (changed) lines.push(`Changed ${pluralize(changed, noun)} for ${personLabel}`);
  return lines;
}

function describePersonChanges(
  form: Person | undefined,
  baseline: Person | undefined,
  personLabel: string,
): string[] {
  const current = ensurePersonContactArrays(form ?? emptyPersonStub());
  const before = ensurePersonContactArrays(baseline ?? emptyPersonStub());
  const lines: string[] = [];

  const nameOf = (p: Person) =>
    [(p.title ?? "MR").trim(), (p.forename ?? "").trim(), (p.surname ?? "").trim()].join(" ").trim();
  if (nameOf(current) !== nameOf(before)) {
    lines.push(`Changed the name of ${personLabel}`);
  }

  lines.push(
    ...describeContactPointChanges(
      "email address",
      personLabel,
      (current.emailAddresses ?? []).map((em) => ({
        value: normalizeEmailForCompare(em?.emailAddress),
        deleted: isContactPointDeleted(em),
      })),
      (before.emailAddresses ?? []).map((em) => ({
        value: normalizeEmailForCompare(em?.emailAddress),
      })),
    ),
  );
  lines.push(
    ...describeContactPointChanges(
      "phone number",
      personLabel,
      (current.phoneNumbers ?? []).map((ph) => ({
        value: normalizePhoneForCompare(buildFullPhoneNumber(ph)),
        deleted: isContactPointDeleted(ph),
      })),
      (before.phoneNumbers ?? []).map((ph) => ({
        value: normalizePhoneForCompare(buildFullPhoneNumber(ph)),
      })),
    ),
  );

  return lines;
}

/** Address is one idea to a user, so any of its parts moving reads as a single change. */
const ADDRESS_COMPARE_FIELDS = [
  "addressSubDwelling",
  "addressNameNumber",
  "street",
  "locality",
  "town",
  "county",
  "postcode",
  "countryCode",
  "uprn",
] as const;

const CONTACT_FIELD_CHANGE_LABELS: Array<[keyof ContactDetails, string]> = [
  ["category", "Changed the contact type"],
  ["companyName", "Changed the company name"],
  ["notes", "Edited the contact note"],
  ["salutationLine", "Edited the salutation"],
  ["intentionName", "Changed the intention"],
  ["leadSourceName", "Changed the lead source"],
  ["positionName", "Changed the position"],
];

/**
 * Plain-English list of what the user changed since the contact was loaded — shown on
 * the save button so "there are changes" is never something they have to take on trust.
 */
export function describeContactEditChanges(
  form: ContactDetails,
  baseline: ContactDetails,
  ctx: { secondPersonEnabled: boolean; baselineSecondPersonEnabled: boolean },
): string[] {
  const lines: string[] = [];
  const textOf = (value: unknown) => (value == null ? "" : String(value).trim());

  for (const [field, label] of CONTACT_FIELD_CHANGE_LABELS) {
    if (textOf(form[field]) !== textOf(baseline[field])) lines.push(label);
  }

  if (
    ADDRESS_COMPARE_FIELDS.some((field) => textOf(form[field]) !== textOf(baseline[field]))
  ) {
    lines.push("Updated the address");
  }

  lines.push(...describePersonChanges(form.person?.[0], baseline.person?.[0], "the first person"));

  if (ctx.secondPersonEnabled !== ctx.baselineSecondPersonEnabled) {
    lines.push(ctx.secondPersonEnabled ? "Added a second person" : "Removed the second person");
  } else if (ctx.secondPersonEnabled) {
    lines.push(
      ...describePersonChanges(form.person?.[1], baseline.person?.[1], "the second person"),
    );
  }

  if (
    JSON.stringify(snapshotApplicantRequirementsForCompare(form.applicantRequirements)) !==
    JSON.stringify(snapshotApplicantRequirementsForCompare(baseline.applicantRequirements))
  ) {
    lines.push("Updated the applicant requirements");
  }

  return lines;
}

export function buildContactUsersPayload(
  user: ContactAssignedUser | null,
): ContactCreateUserRef[] {
  if (!user?.id) return [];
  return [
    {
      id: user.id,
      externalId: user.externalId,
      name: user.name,
    },
  ];
}

export function buildContactUpdatePatch(
  form: ContactCreateFormInput & { archived?: boolean },
  current?: ContactDetails | null,
  ctx?: { secondPersonEnabled?: boolean },
): Record<string, unknown> {
  const primary = form.person?.[0];
  if (!primary) {
    throw new Error("At least one person is required");
  }

  const secondPersonEnabled = ctx?.secondPersonEnabled ?? false;
  if (
    !form.person?.some((p, index) =>
      shouldIncludePersonInCreatePayload(p as Person, index, secondPersonEnabled),
    )
  ) {
    throw new Error(SURNAME_REQUIRED_MESSAGE);
  }

  const forename = (primary.forename ?? "").trim();
  const surname = (primary.surname ?? "").trim();
  const name = `${forename} ${surname}`.trim();
  const email = firstLiveEmailAddress(primary.emailAddresses);
  const phone = firstLivePhoneNumber(primary.phoneNumbers);

  const emailUnchanged =
    Boolean(current) &&
    normalizeEmailForCompare(email) ===
    normalizeEmailForCompare(
      current?.email ?? firstLiveEmailAddress(current?.person?.[0]?.emailAddresses),
    );
  const phoneUnchanged =
    Boolean(current) &&
    normalizePhoneForCompare(phone) ===
    normalizePhoneForCompare(
      current?.mobilePhone ?? firstLivePhoneNumber(current?.person?.[0]?.phoneNumbers),
    );

  // Neither `emailAddresses` nor `phoneNumbers` is restored from `current`: the form
  // carries every row, so the built arrays are authoritative. Restoring the server's
  // rows would drop rows added below the first (the unchanged checks only look at row
  // 0) and would drop the `isDeleted` markers on pending removals.
  const person = (form.person ?? [])
    .map((p, index) => {
      if (!shouldIncludePersonInCreatePayload(p as Person, index, secondPersonEnabled)) return null;
      return buildPersonForApi(p as Person, current?.person?.[index]);
    })
    .filter((p): p is NonNullable<typeof p> => p != null);

  const patch: Record<string, unknown> = {
    name,
    forename,
    surname,
    title: primary.title ?? current?.title ?? null,
    email: emailUnchanged ? undefined : email || null,
    mobilePhone: phoneUnchanged ? undefined : phone || null,
    person,
    addressSubDwelling: trimOrNull(form.addressSubDwelling),
    addressNameNumber: trimOrNull(form.addressNameNumber),
    street: trimOrNull(form.street),
    locality: trimOrNull(form.locality),
    town: trimOrNull(form.town),
    county: trimOrNull(form.county),
    postcode: trimOrNull(form.postcode),
    countryCode: normalizeCountryCode(form.countryCode) || null,
    uprn: trimOrNull(form.uprn ?? undefined),
    ...omitEmpty({
      intentionCode:
        normalizeStoredCode(form.intentionCode) ??
        intentionCodeFromName(form.intentionName) ??
        "0",
      positionCode: normalizeStoredCode(form.positionCode),
      positionName: trimOrNull(form.positionName),
      leadSourceCode:
        normalizeStoredCode(form.leadSourceCode) ??
        leadSourceCodeFromName(form.leadSourceName) ??
        "0",
    }),
  };

  if (emailUnchanged) delete patch.email;
  if (phoneUnchanged) delete patch.mobilePhone;

  if (contactCategorySupportsRequirements(form.category)) {
    const requirements = buildApplicantRequirementsForApi(
      form.applicantRequirements,
      current?.applicantRequirements,
    );
    if (requirements.length) {
      patch.applicantRequirements = requirements;
    }
  }

  return patch;
}

/** List API may return null summary fields even when person was saved — fill from POST body. */
export function enrichContactSummary(
  response: ContactSummary,
  payload: ContactCreatePayload,
): ContactSummary {
  const primary = payload.person?.[0];
  const name = primary ? `${primary.forename} ${primary.surname}`.trim() : null;
  const email = primary?.emailAddresses?.[0]?.emailAddress ?? null;
  const phone = primary?.phoneNumbers?.[0]?.number ?? null;

  return {
    ...response,
    sourceCrm: response.sourceCrm?.trim() || payload.sourceCrm,
    name: response.name ?? name,
    forename: response.forename ?? primary?.forename?.trim() ?? null,
    surname: response.surname ?? primary?.surname?.trim() ?? null,
    email: response.email ?? email,
    mobilePhone: response.mobilePhone ?? phone,
    town: response.town ?? payload.town ?? null,
    postcode: response.postcode ?? payload.postcode ?? null,
    category: response.category ?? payload.category ?? null,
    archived: response.archived ?? false,
  };
}
